import { useState, useEffect, type ReactNode } from 'react';
import {
  X,
  Plus,
  Users,
  Calendar,
  Clock,
  Lock,
  Archive,
  CalendarPlus,
  Pencil,
  Loader2,
  Home,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';
import {
  TurfStatus,
  CanvasserType,
  ShiftStatus,
  getWalkbookTurfStatus,
  pickCurrentShift,
  groupShiftsIntoSessions,
  type CampaignUserDto,
  type ShiftListItemDto,
} from '@signalvote/shared';

import { match, skip, type CurrentRead } from '@signalvote/data';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { BoundaryEditIcon } from './BoundaryEditIcon';
import { Input } from '@/components/ui/input';
import { useFieldOpsStore } from '@/stores/fieldOpsStore';
import { useCampaignStore } from '@/stores';
import {
  useWalkbookDetail,
  useShifts,
  useCreateShift,
  useUpdateShift,
  useVolunteers,
  useVendors,
  useAssignVolunteers,
  useRemoveAssignment,
  useArchiveWalkbook,
  useUpdateWalkbook,
} from '@/hooks/queries';
import {
  getCampaignTodayString,
  formatTime12Hour,
  toTimeStringInTimezone,
} from '@/lib/calendarUtils';
import { CanvasserTypeBadge } from '@/components/admin/staff/shared';
import { TURF_STATUS_DISPLAY, getPaceLabel } from './turfStatusDisplay';
import { readyStaff } from '@/lib/staffRead';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getVolunteerDisplayName(vol: { firstName?: string; lastName?: string; name?: string; email?: string }): string {
  if (vol.name) return vol.name;
  return [vol.firstName, vol.lastName].filter(Boolean).join(' ') || vol.email || 'Unknown';
}

/** Friendly session date: "Today" in campaign tz, else "Sat, Aug 22". */
function formatSessionWhen(date: string, today: string): string {
  if (date === today) return 'Today';
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  // Construct from explicit parts (a pure label, not "today" logic) to avoid the
  // UTC-midnight shift that `new Date('YYYY-MM-DD')` would introduce.
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** e.g. 120 → "2h", 90 → "1.5h". */
function formatDurationLabel(minutes: number): string {
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
}

/** Parse an hours-string form field ("2", "1.5") into estimated minutes. */
function hoursToMinutes(hours: string): number {
  return Math.round(parseFloat(hours) * 60);
}

/** Format a StartedAt ISO stamp as a campaign-tz clock time, e.g. "9:04 AM". */
function formatStartedAt(iso: string | null | undefined, timeZoneId?: string): string | null {
  if (!iso) return null;
  return formatTime12Hour(toTimeStringInTimezone(iso, timeZoneId));
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TurfDetailPanelProps {
  onEditPolygon: (turfId: string) => void;
  onBack: () => void;
}

/** The turf's status line: known only once its shifts are; the reason for "unknown" shows in the session zone. */
type TurfHeadline =
  | { kind: 'known'; status: TurfStatus; pace: ReturnType<typeof getPaceLabel> }
  | { kind: 'checking' }
  | { kind: 'unknown' };

// The three inline editors the session/people surface can open. Mutually exclusive.
type PanelMode = 'idle' | 'add' | 'schedule' | 'reschedule';

/** Without the turf's shifts the panel can't tell "no session" from "unknown", so it says which. */
const SESSIONS_UNKNOWN: Record<Exclude<CurrentRead<unknown>['status'], 'ready'>, string> = {
  idle: 'Loading sessions…',
  loading: 'Loading sessions…',
  offline: "You're offline, so this turf's sessions can't load.",
  failed: "Couldn't load this turf's sessions. Try again shortly.",
};

// ---------------------------------------------------------------------------
// Main component — the merged "who's working this turf" surface.
//
// One turf → one current session → N people. The people roster is the primary
// block (the one thing an organizer manages); the session is a context strip
// above it. The panel schedules and reschedules; it never starts a session —
// canvassers do that in the field, so a live session shows read-only here.
// ---------------------------------------------------------------------------

export function TurfDetailPanel({ onEditPolygon, onBack }: TurfDetailPanelProps) {
  const { selectedTurfId, selectTurf } = useFieldOpsStore();
  const timeZoneId = useCampaignStore((s) => s.selectedCampaign?.timeZoneId);
  const [mode, setMode] = useState<PanelMode>('idle');
  const [typeFilter, setTypeFilter] = useState<'all' | CanvasserType>('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [editingName, setEditingName] = useState<string | null>(null);

  // Data hooks
  const walkbookRead = useWalkbookDetail(selectedTurfId ?? skip);
  const shiftsRead = useShifts(selectedTurfId ? { walkbookId: selectedTurfId } : skip);
  const allVolunteers = readyStaff(useVolunteers(undefined));
  const { data: vendors = [] } = useVendors();

  // Mutations
  const { mutate: assignVolunteers } = useAssignVolunteers();
  const { mutateAsync: createShift } = useCreateShift();
  const { mutateAsync: updateShift } = useUpdateShift();
  const {
    mutate: removeAssignment,
    isPending: removing,
    variables: removingVars,
  } = useRemoveAssignment();
  const { mutate: archiveWalkbook, isPending: archiving } = useArchiveWalkbook();
  const { mutateAsync: updateWalkbook } = useUpdateWalkbook();

  // Reset inline editors when the selected turf changes.
  useEffect(() => {
    setMode('idle');
    setEditingName(null);
  }, [selectedTurfId]);

  // --- Early returns (all hooks are above) ---

  if (!selectedTurfId) return null;

  if (walkbookRead.status === 'loading' || walkbookRead.status === 'idle') {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (walkbookRead.status !== 'ready') {
    return (
      <p role="status" className="px-4 py-6 text-center text-xs text-muted-foreground">
        {walkbookRead.status === 'offline' ? "You're offline, so this turf can't load." : "Couldn't load this turf. Try again shortly."}
      </p>
    );
  }

  const walkbook = walkbookRead.data;
  const progress = walkbook.progress;
  const assignments = walkbook.assignments ?? [];
  const shifts = shiftsRead.status === 'ready' ? shiftsRead.data.items : [];
  const hasPeople = assignments.length > 0;

  // The ONE representative session (the shared current-shift pick) and its
  // fan-out group. People and progress come from the assignment list, never from
  // this shift.
  const today = getCampaignTodayString();
  const currentShift = pickCurrentShift(shifts, today);
  const currentShiftStatus = currentShift?.effectiveStatus;
  const sessions = groupShiftsIntoSessions(shifts);
  const currentSession = currentShift
    ? sessions.find((s) => s.shifts.some((sh) => sh.id === currentShift.id)) ?? null
    : null;

  // Reconciled, session-primary headline: a live session always wins, so without the shifts
  // there is no headline to give, only "still checking" or "unknown".
  const headline = match<CurrentRead<unknown>, TurfHeadline>(shiftsRead, {
    ready: () => {
      const status = getWalkbookTurfStatus(
        { assignedVolunteers: assignments.map((a) => ({ id: a.volunteerId })), progress },
        currentShiftStatus,
      );
      return { kind: 'known', status, pace: getPaceLabel(progress, status) };
    },
    idle: () => ({ kind: 'checking' }),
    loading: () => ({ kind: 'checking' }),
    failed: () => ({ kind: 'unknown' }),
    offline: () => ({ kind: 'unknown' }),
  });
  const pace = headline.kind === 'known' ? headline.pace : null;

  // Cross-reference each co-assignee with volunteer data for type/vendor badges.
  const volunteerById = new Map(allVolunteers.map((v) => [v.userId, v]));
  // The row mid-removal: react-query hands back the in-flight variables, so no
  // extra state is needed to know which person is on the way out.
  const removingVolunteerId = removing ? removingVars?.volunteerId : undefined;
  const availableVolunteers = allVolunteers.filter(
    (v) => v.canvasserType && !assignments.some((a) => a.volunteerId === v.userId),
  );

  // --- Handlers ---

  const handleAssignVolunteer = (volunteerId: string) => {
    // A single additive assign carrying only the new id — the backend owns every
    // shift side-effect (fan out the open shift to the new co-assignee, copy an
    // in-progress source). The adder stays open for a further add.
    assignVolunteers({ id: walkbook.id, volunteerIds: [volunteerId] });
  };

  const handleRemoveVolunteer = (volunteerId: string) => {
    removeAssignment({ walkbookId: walkbook.id, volunteerId });
  };

  const handleArchive = () => {
    archiveWalkbook(walkbook.id, { onSuccess: () => selectTurf(null) });
  };

  // `name` is the full action, set only where the cell's label is shortened —
  // it becomes the accessible name and the tooltip.
  const footerActions: Array<{
    label: string;
    name?: string;
    icon: ReactNode;
    onClick: () => void;
    disabled?: boolean;
    destructive?: boolean;
  }> = [
    {
      label: 'New shift',
      icon: <CalendarPlus className="h-[17px] w-[17px]" aria-hidden />,
      onClick: () => setMode('schedule'),
    },
    {
      name: 'Edit boundary',
      label: 'Edit area',
      icon: <BoundaryEditIcon className="h-[19px] w-[19px]" />,
      onClick: () => onEditPolygon(walkbook.id),
    },
    {
      name: 'Archive turf',
      label: 'Archive',
      icon: <Archive className="h-[17px] w-[17px]" aria-hidden />,
      onClick: handleArchive,
      disabled: archiving,
      destructive: true,
    },
  ];

  const renderSessionZone = () => {
    if (mode === 'reschedule' && currentSession) {
      // Editable fields are name/date/time/duration, never who's on the shift.
      // Allowed while the session is open OR in progress; Completed is locked
      // upstream, so this never opens for it.
      const live = currentShiftStatus === ShiftStatus.InProgress;
      const representative = currentSession.shifts[0];
      return (
        <SessionForm
          // Distinct from the schedule form's key: same component, same slot, so
          // without this React reuses the instance and keeps the old field values.
          key="reschedule"
          title={live ? 'Reschedule live session' : 'Reschedule session'}
          initial={{
            name: representative?.name ?? '',
            date: currentSession.date,
            startTime: currentSession.startTime,
            hours: representative ? String(representative.estimatedMinutes / 60) : '2',
          }}
          nameFallback={representative?.name ?? ''}
          submitLabel="Save"
          banner={
            live ? (
              <p className="mb-2 rounded-lg bg-blue-50 px-2.5 py-1.5 text-[11px] text-blue-700">
                Editing a session that&apos;s already in progress is allowed — the time is a soft
                label; the real start is stamped when work began.
              </p>
            ) : undefined
          }
          onSubmit={async (fields) => {
            // Move every copy of the one session so the group stays together;
            // reschedule carries only schedule fields, never a re-staffing.
            const results = await Promise.allSettled(
              currentSession.shifts.map((sh) => updateShift({ row: sh, ...fields, walkbookId: null, assignedToId: null })),
            );
            if (results.every((r) => r.status === 'fulfilled')) setMode('idle');
          }}
          onCancel={() => setMode('idle')}
        />
      );
    }

    if (mode === 'schedule') {
      // Always mints an UPCOMING shift (Available/Claimed), never InProgress —
      // the canvasser moves it to Live in the field.
      return (
        <SessionForm
          key="schedule"
          title="Schedule a session"
          initial={{ name: '', date: '', startTime: '09:00', hours: '2' }}
          nameFallback={`${walkbook.name} shift`}
          submitLabel="Schedule"
          submitIcon={<Calendar className="mr-1.5 h-3.5 w-3.5" />}
          note={
            <p className="text-[11px] text-gray-400">
              Canvassers start the shift themselves in the field.
            </p>
          }
          onSubmit={async (fields) => {
            // One copy per co-assignee (each pre-targeted, as the backend requires
            // on a shared turf); with no co-assignee a single untargeted shift.
            const coAssigneeIds = assignments.map((a) => a.volunteerId);
            const targets: Array<string | null> = coAssigneeIds.length > 0 ? coAssigneeIds : [null];
            const results = await Promise.allSettled(
              targets.map((id) =>
                createShift({ ...fields, walkbookId: walkbook.id, assignedToId: id }),
              ),
            );
            if (results.every((r) => r.status === 'fulfilled')) setMode('idle');
          }}
          onCancel={() => setMode('idle')}
        />
      );
    }

    if (shiftsRead.status !== 'ready') {
      return (
        <p role="status" className="rounded-xl border border-dashed px-3 py-3 text-center text-xs text-muted-foreground">
          {SESSIONS_UNKNOWN[shiftsRead.status]}
        </p>
      );
    }

    if (currentShift) {
      return (
        <SessionStrip
          shift={currentShift}
          today={today}
          timeZoneId={timeZoneId}
          onReschedule={() => setMode('reschedule')}
        />
      );
    }

    // No session: cold start (people) makes scheduling the primary CTA; empty
    // keeps it secondary so "Add the first canvasser" leads.
    return (
      <button
        type="button"
        onClick={() => setMode('schedule')}
        className={cn(
          'flex w-full items-center justify-center gap-2 rounded-xl px-3 py-3 text-xs font-medium transition-colors',
          hasPeople
            ? 'bg-primary text-white hover:brightness-105'
            : 'border border-dashed border-primary/30 bg-primary/5 text-primary hover:border-primary/50 hover:bg-primary/10',
        )}
      >
        <Calendar className="h-3.5 w-3.5" />
        Schedule a session
      </button>
    );
  };

  return (
    <div className="flex max-h-[calc(100vh-8rem)] flex-col">
      {/* Back row + header */}
      <div className="px-4 pt-3 pb-3">
        <button
          type="button"
          onClick={onBack}
          className="mb-3 flex w-full items-center gap-2 rounded-lg px-1 py-1.5 text-left transition-colors hover:bg-gray-50"
        >
          <ChevronLeft className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-600">All Turfs</span>
        </button>
        <div>
          {editingName !== null ? (
            <Input
              autoFocus
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onBlur={() => setEditingName(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const trimmed = editingName.trim();
                  if (trimmed && trimmed !== walkbook.name) {
                    updateWalkbook({ id: walkbook.id, name: trimmed });
                  }
                  setEditingName(null);
                } else if (e.key === 'Escape') {
                  setEditingName(null);
                }
              }}
              className="h-8 text-lg font-bold"
            />
          ) : (
            <div
              className="group flex cursor-text items-center gap-1.5 rounded hover:bg-gray-50"
              onClick={() => setEditingName(walkbook.name)}
              title="Click to rename"
            >
              <h3 className="truncate text-lg font-bold text-gray-900">{walkbook.name}</h3>
              <Pencil
                aria-hidden
                className="h-3.5 w-3.5 shrink-0 text-gray-300 opacity-0 transition-opacity group-hover:opacity-100"
              />
            </div>
          )}
          <div className="mt-1 flex items-center gap-1.5">
            <span
              data-testid="turf-status-dot"
              className={cn(
                'inline-block h-2 w-2 rounded-full',
                headline.kind === 'known' ? TURF_STATUS_DISPLAY[headline.status].bg : 'bg-turf-unknown',
              )}
            />
            <span
              className={cn(
                'text-xs font-medium',
                headline.kind === 'known' ? TURF_STATUS_DISPLAY[headline.status].text : 'text-gray-500',
              )}
            >
              {headline.kind === 'known'
                ? TURF_STATUS_DISPLAY[headline.status].label
                : headline.kind === 'checking'
                  ? 'Checking…'
                  : 'Status unknown'}
            </span>
          </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-2">
        {/* Stat row */}
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Home className="h-3.5 w-3.5" />
            <span className="font-semibold text-gray-900">{walkbook.houseCount}</span> houses
          </span>
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            <span className="font-semibold text-gray-900">{walkbook.voterCount}</span> voters
          </span>
        </div>

        {/* Progress — stays in the turf header, only when > 0 */}
        {progress > 0 && (
          <div className="mt-3 space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-gray-900">{progress}%</span>
              {pace && <span className={cn('font-medium', pace.className)}>{pace.label}</span>}
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-primary transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Current session strip / schedule / reschedule */}
        <div className="mt-4">{renderSessionZone()}</div>

        <div className="my-4 h-px bg-gray-100" />

        {/* People on this turf */}
        <section className="space-y-2">
          <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <Users className="h-3.5 w-3.5" />
            Who&apos;s working this turf
          </h4>

          {hasPeople ? (
            <div className="overflow-hidden rounded-xl border border-gray-100">
              {assignments.map((a) => {
                const vol = volunteerById.get(a.volunteerId);
                const isRemoving = a.volunteerId === removingVolunteerId;
                return (
                  <div
                    key={a.volunteerId}
                    data-testid="turf-assignee-row"
                    className={cn(
                      'group flex items-center gap-2.5 border-b border-gray-100 bg-white px-3 py-2 last:border-b-0 transition-opacity',
                      isRemoving && 'opacity-50',
                    )}
                  >
                    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
                      {getInitials(a.volunteerName)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-800">
                        {a.volunteerName}
                      </span>
                      {vol && (
                        <span className="flex items-center gap-1.5">
                          <CanvasserTypeBadge type={vol.canvasserType} />
                          {vol.vendorName && (
                            <span className="text-[11px] text-gray-400">{vol.vendorName}</span>
                          )}
                        </span>
                      )}
                    </div>
                    <button
                      title={isRemoving ? `Removing ${a.volunteerName}\u2026` : `Remove ${a.volunteerName}`}
                      className={cn(
                        'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-gray-300 transition-all hover:bg-red-50 hover:text-red-500',
                        isRemoving ? 'opacity-100 text-red-500' : 'opacity-0 group-hover:opacity-100',
                      )}
                      onClick={() => handleRemoveVolunteer(a.volunteerId)}
                      disabled={removing}
                    >
                      {isRemoving ? (
                        <Loader2 data-testid="removing-spinner" className="h-3 w-3 animate-spin" />
                      ) : (
                        <X className="h-3 w-3" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="py-3 text-center text-xs text-gray-400">
              Nobody yet — add the first canvasser to this turf.
            </p>
          )}

          {mode === 'add' ? (
            <InlineAssignmentAdder
              availableVolunteers={availableVolunteers}
              onAssign={handleAssignVolunteer}
              onDone={() => setMode('idle')}
              typeFilter={typeFilter}
              vendorFilter={vendorFilter}
              onTypeFilterChange={setTypeFilter}
              onVendorFilterChange={setVendorFilter}
              vendors={vendors}
            />
          ) : (
            <button
              type="button"
              onClick={() => setMode('add')}
              className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2.5 text-xs font-medium text-white transition-colors hover:brightness-105"
            >
              <Plus className="h-3.5 w-3.5" />
              {hasPeople ? 'Add someone' : 'Add the first canvasser'}
            </button>
          )}
        </section>

        <div className="h-2" />
      </div>

      {/* Footer — three equal columns, icon over label. All three actions stay
          visible at the panel's ~300px width; a one-item overflow menu was pure
          friction. Further lifecycle actions belong in a header menu, not here. */}
      <div className="grid shrink-0 grid-cols-3 gap-[2px] border-t border-gray-100 p-1.5">
        {footerActions.map((action) => (
          <button
            key={action.label}
            type="button"
            aria-label={action.name ?? action.label}
            title={action.name}
            onClick={action.onClick}
            disabled={action.disabled}
            className={cn(
              'turf-action-cell group flex flex-col items-center gap-[5px] rounded-md px-[2px] py-2',
              'text-xs text-gray-700 transition-colors duration-[120ms]',
              'hover:bg-gray-100 active:bg-gray-200 disabled:opacity-45 disabled:hover:bg-transparent',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              action.destructive && 'hover:text-destructive',
            )}
          >
            {/* Fixed icon row so the three labels share a baseline — the
                boundary badge overhangs its glyph box. */}
            <span
              className={cn(
                'flex h-[19px] items-center text-gray-500 transition-colors duration-[120ms]',
                action.destructive && 'group-hover:text-destructive',
              )}
            >
              {action.icon}
            </span>
            <span className="w-full truncate">{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SessionStrip — the ONE current session as a thin context strip
// ---------------------------------------------------------------------------

function SessionStrip({
  shift,
  today,
  timeZoneId,
  onReschedule,
}: {
  shift: ShiftListItemDto;
  today: string;
  timeZoneId?: string;
  onReschedule: () => void;
}) {
  const eff = shift.effectiveStatus;
  const isLive = eff === ShiftStatus.InProgress;
  const isDone = eff === ShiftStatus.Completed;
  const isNoShow = eff === 'NoShow';
  const startedAt = formatStartedAt(shift.startedAt, timeZoneId);

  const when = formatSessionWhen(shift.date, today);
  const time = formatTime12Hour(shift.startTime);

  return (
    <div
      className={cn(
        'rounded-xl border px-3 py-2.5',
        isLive
          ? 'border-blue-200 bg-blue-50/60'
          : isDone
            ? 'border-green-200 bg-green-50/50'
            : 'border-gray-200 bg-gray-50/60',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          {isLive ? (
            <span className="flex items-center gap-1.5 text-[11px] font-bold text-blue-600">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-blue-500" />
              LIVE{startedAt && ` · started ${startedAt}`}
            </span>
          ) : (
            <span
              className={cn(
                'flex items-center gap-1 text-[11px] font-semibold',
                isNoShow ? 'text-yellow-700' : 'text-gray-500',
              )}
            >
              <Clock className="h-3 w-3" />
              {isDone ? 'Completed today' : isNoShow ? 'Missed' : 'Upcoming'}
            </span>
          )}
          <p className="mt-0.5 truncate text-sm font-semibold text-gray-800">
            {when} · {time}
          </p>
          <p className="truncate text-[11px] text-gray-400">
            {formatDurationLabel(shift.estimatedMinutes)} · {shift.name}
          </p>
        </div>

        {isDone ? (
          <span className="flex flex-shrink-0 items-center gap-1 text-[11px] text-gray-400">
            <Lock className="h-3 w-3" />
            locked
          </span>
        ) : (
          <button
            type="button"
            onClick={onReschedule}
            className="flex-shrink-0 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-600 transition-colors hover:bg-gray-50"
          >
            Reschedule
          </button>
        )}
      </div>

      {/* Passive calendar indicator — the same object the calendar shows. */}
      <div className="mt-2">
        <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] text-gray-500">
          <Calendar className="h-3 w-3" />
          on the campaign calendar
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SessionForm — the shared schedule/reschedule editor
//
// Scheduling and rescheduling ask for exactly the same four things (name, date,
// start time, duration) and differ only in what they start from, what the
// submit button says, and the surrounding copy. They were two near-identical
// components, so a change to the field layout had to be made twice.
//
// The caller owns the write and decides whether to close; this component owns
// the field state and the in-flight flag.
// ---------------------------------------------------------------------------

interface SessionFormFields {
  name: string;
  date: string;
  startTime: string;
  estimatedMinutes: number;
}

function SessionForm({
  title,
  initial,
  nameFallback,
  banner,
  note,
  submitLabel,
  submitIcon,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial: { name: string; date: string; startTime: string; hours: string };
  /** Shown as the placeholder, and used as the name when the field is left blank. */
  nameFallback: string;
  banner?: ReactNode;
  note?: ReactNode;
  submitLabel: string;
  submitIcon?: ReactNode;
  onSubmit: (fields: SessionFormFields) => Promise<void> | void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [date, setDate] = useState(initial.date);
  const [startTime, setStartTime] = useState(initial.startTime);
  const [hours, setHours] = useState(initial.hours);
  const [pending, setPending] = useState(false);

  const handleSubmit = async () => {
    if (!date) return;
    setPending(true);
    try {
      await onSubmit({
        name: name.trim() || nameFallback,
        date,
        startTime,
        estimatedMinutes: hoursToMinutes(hours),
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          {title}
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {banner}

      {/* Date and time stay stacked: sharing a flex row in this ~20rem panel,
          the native controls can't shrink to their share and the time picker's
          right edge gets clipped. */}
      <div className="space-y-2">
        <Input
          placeholder={nameFallback}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-8 rounded-lg border-gray-200 bg-white text-xs"
        />
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-8 rounded-lg border-gray-200 bg-white text-xs"
        />
        {/* .time-input--icon-aligned lines the clock up with the calendar
            icon above it; see index.css. */}
        <Input
          type="time"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
          className="h-8 rounded-lg border-gray-200 bg-white text-xs time-input--icon-aligned"
        />
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="0.5"
              max="8"
              step="0.5"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="h-8 w-16 rounded-lg border-gray-200 bg-white text-xs"
            />
            <span className="text-[11px] text-gray-400">hrs</span>
          </div>
          <Button
            className="h-8 flex-1 rounded-lg text-xs"
            onClick={handleSubmit}
            disabled={!date || pending}
          >
            {pending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <>
                {submitIcon}
                {submitLabel}
              </>
            )}
          </Button>
        </div>
        {note}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// InlineAssignmentAdder — people list with type/vendor filters
//
// One Add-someone action: clicking a person fires a single additive assign and
// the adder stays open, so an organizer can add several people in one pass.
// ---------------------------------------------------------------------------

function InlineAssignmentAdder({
  availableVolunteers,
  onAssign,
  onDone,
  typeFilter,
  vendorFilter,
  onTypeFilterChange,
  onVendorFilterChange,
  vendors,
}: {
  availableVolunteers: CampaignUserDto[];
  onAssign: (volunteerId: string) => void;
  onDone: () => void;
  typeFilter: 'all' | CanvasserType;
  vendorFilter: string;
  onTypeFilterChange: (v: 'all' | CanvasserType) => void;
  onVendorFilterChange: (v: string) => void;
  vendors: Array<{ id: string; name: string }>;
}) {
  const [search, setSearch] = useState('');

  const filteredVolunteers = availableVolunteers
    .filter((v) => getVolunteerDisplayName(v).toLowerCase().includes(search.toLowerCase()))
    .filter((v) => typeFilter === 'all' || v.canvasserType === typeFilter)
    .filter((v) => vendorFilter === 'all' || v.vendorId === vendorFilter);

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/80">
      <div className="flex items-center justify-between px-3 pt-3 pb-2">
        <span className="text-[11px] font-medium text-gray-500">Add someone</span>
        <button
          type="button"
          onClick={onDone}
          className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="space-y-2 px-3 pb-3">
        {/* Type filter pills */}
        <div className="flex gap-1 rounded-lg bg-gray-200 p-0.5">
          {(['all', CanvasserType.Volunteer, CanvasserType.Paid] as const).map((val) => (
            <button
              key={val}
              type="button"
              className={cn(
                'rounded-md px-3 py-1 text-[11px] font-medium transition-all',
                typeFilter === val
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
              onClick={() => onTypeFilterChange(val)}
            >
              {val === 'all' ? 'All' : val === CanvasserType.Volunteer ? 'Vol' : 'Paid'}
            </button>
          ))}
        </div>

        {/* Vendor dropdown — visible on All and Paid tabs, not Volunteer */}
        {vendors.length > 0 && typeFilter !== CanvasserType.Volunteer && (
          <select
            value={vendorFilter}
            onChange={(e) => onVendorFilterChange(e.target.value)}
            className="h-7 w-full rounded-lg border border-gray-300 bg-white px-2 text-xs text-gray-700"
          >
            <option value="all">All vendors</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        )}

        {/* Search input */}
        <Input
          placeholder="Search people..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 rounded-lg border-gray-300 bg-white text-xs"
          autoFocus
        />

        {/* People list */}
        <div className="max-h-36 space-y-1 overflow-y-auto">
          {filteredVolunteers.length === 0 ? (
            <p className="py-4 text-center text-xs text-gray-400">
              {availableVolunteers.length === 0 ? 'Everyone is on this turf' : 'No matches'}
            </p>
          ) : (
            filteredVolunteers.map((vol) => {
              const displayName = getVolunteerDisplayName(vol);
              return (
                <button
                  key={vol.userId}
                  type="button"
                  className="group flex w-full items-center gap-2.5 rounded-lg bg-white px-3 py-2 text-left transition-all hover:shadow-sm"
                  onClick={() => onAssign(vol.userId)}
                >
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
                    {getInitials(displayName)}
                  </div>
                  <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-700">
                    {displayName}
                  </span>
                  <CanvasserTypeBadge type={vol.canvasserType} />
                  <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 transition-colors group-hover:text-primary" />
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
